// frontend/src/components/TripSelector.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from '../axiosConfig';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Select from 'react-select';
import WebSocketEvents from '../services/WebSocketEvents';
import { formatDate, formatTimeString } from '../utils/dateTimeFormatter';
import { usePreferences } from '../contexts/PreferenceContext';
import { useTheme } from '../contexts/ThemeContext';
import translationService from '../services/translationService';

const GROUP_ORDER = ['now', 'today', 'upcoming', 'earlier_today', 'past'];
const ACTIVE_STATUSES = new Set(['in_progress', 'dispatched']);
const HISTORY_STATUSES = ['completed', 'canceled', 'scheduled', 'dispatched', 'in_progress'];
const EMPTY_TRIP_STATUSES = Object.freeze([]);

const getDefaultHistoryFilters = () => ({
    routeId: '',
    search: '',
    tripStatus: '',
    startDate: '',
    endDate: ''
});

const buildSelectorRequestKey = ({
    entityId,
    relevanceMode,
    externalSelectedTripId = '',
    historyFilters = getDefaultHistoryFilters(),
    projection = null,
    includeSeries,
    tripStatuses = EMPTY_TRIP_STATUSES,
    refreshKey = 0,
    language,
    multiLanguageEnabled,
    defaultLanguage
}) => JSON.stringify({
    entityId: entityId?.toString() || '',
    relevanceMode,
    externalSelectedTripId: externalSelectedTripId?.toString() || '',
    routeId: historyFilters.routeId || '',
    search: historyFilters.search || '',
    tripStatus: historyFilters.tripStatus || '',
    startDate: historyFilters.startDate || '',
    endDate: historyFilters.endDate || '',
    projection: projection || '',
    includeSeries: includeSeries ?? '',
    tripStatuses: tripStatuses.join(','),
    refreshKey,
    language: language || '',
    multiLanguageEnabled: multiLanguageEnabled === true,
    defaultLanguage: defaultLanguage || 'en'
});

const buildSelectorInputKey = (options) => buildSelectorRequestKey({
    ...options,
    externalSelectedTripId: ''
});

const normalizeRouteLabelPart = (value) => (
    typeof value === 'string'
        ? value.replace(/\s+/g, ' ').trim()
        : ''
);

const formatHistoryRouteOptionLabel = (route) => {
    const routeName = normalizeRouteLabelPart(route?.name);
    return routeName || normalizeRouteLabelPart(route?.description);
};

const TripSelector = ({
    onSelectTrip,
    currentEntity,
    selectedTripId: externalSelectedTripId,
    selectedTrip,
    allowHistory = true,
    selectionMode = 'detail',
    projection = null,
    includeSeries,
    tripStatuses = EMPTY_TRIP_STATUSES,
    refreshKey = 0,
    onListRefresh,
    onSelectionInvalidated
}) => {
    const { t, i18n } = useTranslation();
    const [trips, setTrips] = useState([]);
    const [selectedTripId, setSelectedTripId] = useState(externalSelectedTripId || '');
    const [isLoadingList, setIsLoadingList] = useState(false);
    const [isLoadingDetails, setIsLoadingDetails] = useState(false);
    const [translatedTrips, setTranslatedTrips] = useState([]);
    const [series, setSeries] = useState([]);
    const [routeOptions, setRouteOptions] = useState([]);
    const [isTripPickerExpanded, setIsTripPickerExpanded] = useState(false);
    const [isHistoryMode, setIsHistoryMode] = useState(false);
    const [historyFilters, setHistoryFilters] = useState(getDefaultHistoryFilters);
    const [selectorPollVersion, setSelectorPollVersion] = useState(0);
    const autoSelectedRef = useRef(false);
    const selectedTripIdRef = useRef('');
    const externalSelectedTripIdRef = useRef(externalSelectedTripId || '');
    const selectorRequestSeqRef = useRef(0);
    const detailRequestSeqRef = useRef(0);
    const pendingDetailRequestRef = useRef(null);
    const suppressNextAutoSelectEchoRef = useRef(null);
    const suppressNextFailedSelectionClearRef = useRef(null);
    const liveStatusVersionRef = useRef(0);
    const latestLiveStatusByTripRef = useRef(new Map());
    const currentLanguageRef = useRef(i18n.language);
    const currentEntityIdRef = useRef(currentEntity?.entityId?.toString() || '');
    const previousLanguageRef = useRef(i18n.language);
    const tripsRef = useRef([]);
    const translatedTripsRef = useRef([]);
    const { entitySettings } = usePreferences();
    const { theme, getColor } = useTheme();
    currentLanguageRef.current = i18n.language;
    currentEntityIdRef.current = currentEntity?.entityId?.toString() || '';
    tripsRef.current = trips;
    translatedTripsRef.current = translatedTrips;

    const isRowEligible = useCallback((trip) => (
        Boolean(trip)
        && (
            tripStatuses.length === 0
            || tripStatuses.includes(trip.status)
        )
    ), [tripStatuses]);

    const resolveTripSelection = useCallback(async (
        tripId,
        {
            resetOnError = true,
            source = 'manual',
            selectedOptionSnapshot = null
        } = {}
    ) => {
        if (!tripId || !currentEntity?.entityId) {
            return;
        }

        if (selectionMode === 'row') {
            const requestTripId = tripId.toString();
            const row = selectedOptionSnapshot?.trip
                || translatedTripsRef.current.find((trip) => trip.id?.toString() === requestTripId)
                || tripsRef.current.find((trip) => trip.id?.toString() === requestTripId);

            detailRequestSeqRef.current += 1;
            pendingDetailRequestRef.current = null;
            setIsLoadingDetails(false);

            if (!isRowEligible(row)) {
                selectedTripIdRef.current = '';
                setSelectedTripId('');
                onSelectTrip(null);
                onSelectionInvalidated?.();
                return;
            }

            selectedTripIdRef.current = tripId;
            setSelectedTripId(tripId);
            onSelectTrip(row);
            return;
        }

        const requestSeq = detailRequestSeqRef.current + 1;
        detailRequestSeqRef.current = requestSeq;
        const requestEntityId = currentEntity.entityId;
        const requestTripId = tripId.toString();
        const requestLanguage = i18n.language;

        pendingDetailRequestRef.current = {
            seq: requestSeq,
            entityId: requestEntityId?.toString(),
            tripId: requestTripId,
            language: requestLanguage,
            source,
            previousExternalSelectedTripId: externalSelectedTripIdRef.current?.toString() || '',
            selectedOptionSnapshot
        };

        const isActivePendingDetailRequest = () => {
            const pending = pendingDetailRequestRef.current;
            return (
                pending?.seq === requestSeq
                && pending?.entityId === requestEntityId?.toString()
                && pending?.tripId === requestTripId
                && pending?.language === requestLanguage
            );
        };

        const isCurrentDetailRequest = () => (
            isActivePendingDetailRequest()
            && detailRequestSeqRef.current === requestSeq
            && currentEntityIdRef.current === requestEntityId?.toString()
            && currentLanguageRef.current === requestLanguage
        );

        selectedTripIdRef.current = tripId;
        setSelectedTripId(tripId);
        setIsLoadingDetails(true);

        try {
            const response = await axios.get(`/api/trips/${requestEntityId}/${tripId}`);

            if (!isCurrentDetailRequest()) {
                return;
            }

            if (response.data.success && response.data.data) {
                const fullTripData = response.data.data;
                const responseEntityId = fullTripData.entity_id ?? fullTripData.entityId;
                if (responseEntityId && responseEntityId.toString() !== requestEntityId?.toString()) {
                    console.warn('[TripSelector] Ignoring trip details for wrong entity', {
                        requestEntityId,
                        responseEntityId,
                        tripId: requestTripId
                    });
                    return;
                }
                onSelectTrip(fullTripData);

                setTrips(prevTrips =>
                    (prevTrips || []).map(trip =>
                        trip.id === fullTripData.id
                            ? {
                                ...trip,
                                status: fullTripData.status,
                                route_name: fullTripData.route_name
                            }
                            : trip
                    )
                );
                setTranslatedTrips(prevTrips =>
                    (prevTrips || []).map(trip =>
                        trip.id === fullTripData.id
                            ? {
                                ...trip,
                                status: fullTripData.status,
                                route_name: trip.route_name || fullTripData.route_name
                            }
                            : trip
                    )
                );
            } else {
                toast.error(t('Failed to load trip details'));
                onSelectTrip(null);
                if (resetOnError) {
                    selectedTripIdRef.current = '';
                    setSelectedTripId('');
                }
            }
        } catch (error) {
            if (!isCurrentDetailRequest()) {
                return;
            }
            console.error('Error fetching trip details:', error);
            toast.error(t('Failed to load trip details'));
            onSelectTrip(null);
            if (resetOnError) {
                selectedTripIdRef.current = '';
                setSelectedTripId('');
            }
        } finally {
            if (isActivePendingDetailRequest()) {
                setIsLoadingDetails(false);
                pendingDetailRequestRef.current = null;
            }
        }
    }, [
        currentEntity?.entityId,
        i18n.language,
        isRowEligible,
        onSelectTrip,
        onSelectionInvalidated,
        selectionMode,
        t
    ]);

    useEffect(() => {
        autoSelectedRef.current = false;
        selectorRequestSeqRef.current += 1;
        detailRequestSeqRef.current += 1;
        pendingDetailRequestRef.current = null;
        suppressNextAutoSelectEchoRef.current = null;
        suppressNextFailedSelectionClearRef.current = null;
        liveStatusVersionRef.current = 0;
        latestLiveStatusByTripRef.current.clear();
        setIsLoadingList(false);
        setIsLoadingDetails(false);
        selectedTripIdRef.current = externalSelectedTripIdRef.current;
        setSelectedTripId(externalSelectedTripIdRef.current);
        setTrips([]);
        setTranslatedTrips([]);
        setSeries([]);
        setRouteOptions([]);
        setIsTripPickerExpanded(false);
        setIsHistoryMode(false);
        setHistoryFilters(getDefaultHistoryFilters());
    }, [currentEntity?.entityId]);

    useEffect(() => {
        const nextExternalTripId = externalSelectedTripId?.toString() || '';
        const pending = pendingDetailRequestRef.current;
        const shouldSuppressStaleParentEcho =
            pending?.source === 'manual'
            && pending.tripId !== nextExternalTripId
            && pending.previousExternalSelectedTripId === nextExternalTripId;

        externalSelectedTripIdRef.current = externalSelectedTripId || '';

        if (shouldSuppressStaleParentEcho) {
            return;
        }

        if (externalSelectedTripId && nextExternalTripId !== selectedTripId?.toString()) {
            if (pending && pending.tripId !== nextExternalTripId) {
                detailRequestSeqRef.current += 1;
                pendingDetailRequestRef.current = null;
                setIsLoadingDetails(false);
            }

            selectedTripIdRef.current = externalSelectedTripId;
            setSelectedTripId(externalSelectedTripId);
            if (!selectedTrip) {
                // Row-only hosts resolve against the selector projection. If
                // the projection has not loaded yet, the list request will
                // validate and publish the row without touching trip detail.
                if (
                    selectionMode === 'row'
                    && tripsRef.current.length === 0
                    && translatedTripsRef.current.length === 0
                ) {
                    return;
                }
                resolveTripSelection(externalSelectedTripId, {
                    resetOnError: false,
                    source: 'external'
                });
            }
        }
    }, [externalSelectedTripId, resolveTripSelection, selectedTrip, selectedTripId, selectionMode]);

    useEffect(() => {
        if (previousLanguageRef.current === i18n.language) {
            return;
        }
        previousLanguageRef.current = i18n.language;

        if (selectedTripIdRef.current && currentEntity?.entityId) {
            resolveTripSelection(selectedTripIdRef.current, {
                resetOnError: false,
                source: 'language'
            });
        }
    }, [currentEntity?.entityId, i18n.language, resolveTripSelection]);

    useEffect(() => {
        const fetchRouteOptions = async () => {
            if (!isHistoryMode || !currentEntity?.entityId) {
                return;
            }

            try {
                const response = await axios.get(`/api/routes/${currentEntity.entityId}`);
                const routes = Array.isArray(response.data) ? response.data : [];
                setRouteOptions(routes.filter(route => !route.status || route.status === 'active'));
            } catch (error) {
                console.error('Error fetching route options:', error);
                setRouteOptions([]);
            }
        };

        fetchRouteOptions();
    }, [currentEntity?.entityId, isHistoryMode]);

    useEffect(() => {
        const translateTrips = async (fetchedTrips) => {
            if (
                entitySettings?.multi_language_enabled !== true
                || !currentEntity?.entityId
                || fetchedTrips.length === 0
                || i18n.language === (entitySettings?.entity_default_language || 'en')
            ) {
                return fetchedTrips;
            }

            const routeItemsById = new Map();
            fetchedTrips.forEach((trip) => {
                const routeId = Number(trip.route_id);
                if (!Number.isFinite(routeId) || routeItemsById.has(routeId)) {
                    return;
                }
                routeItemsById.set(routeId, {
                    id: routeId,
                    fields: {
                        name: trip.route_name || ''
                    }
                });
            });

            const routeItems = Array.from(routeItemsById.values());
            if (routeItems.length === 0) {
                return fetchedTrips;
            }

            const routeTranslations = await translationService.batchGetTranslations(
                currentEntity.entityId,
                'route',
                routeItems,
                i18n.language
            );

            return fetchedTrips.map((trip) => {
                const translation = routeTranslations.get(Number(trip.route_id));
                return {
                    ...trip,
                    route_name: translation?.name?.content || trip.route_name
                };
            });
        };

        const fetchAndTranslateTrips = async () => {
            if (!currentEntity?.entityId) return;

            const relevanceMode = isHistoryMode
                ? 'history'
                : (isTripPickerExpanded ? 'expanded' : 'tracking');
            const selectorInputKey = buildSelectorInputKey({
                entityId: currentEntity.entityId,
                relevanceMode,
                historyFilters,
                projection,
                includeSeries,
                tripStatuses,
                refreshKey,
                language: i18n.language,
                multiLanguageEnabled: entitySettings?.multi_language_enabled,
                defaultLanguage: entitySettings?.entity_default_language
            });

            const failedSelectionClear = suppressNextFailedSelectionClearRef.current;
            if (
                selectionMode === 'row'
                && !externalSelectedTripId
                && failedSelectionClear?.inputKey === selectorInputKey
            ) {
                // A failed row request clears the controlled host selection.
                // Consume that exact state echo without immediately repeating
                // the same failed request and user-facing error notification.
                suppressNextFailedSelectionClearRef.current = null;
                return;
            }
            if (failedSelectionClear) {
                // Do not let a one-shot suppression leak into a later request
                // after another selector input changed.
                suppressNextFailedSelectionClearRef.current = null;
            }

            if (
                relevanceMode === 'tracking'
                && externalSelectedTripId
                && suppressNextAutoSelectEchoRef.current?.tripId?.toString() === externalSelectedTripId.toString()
                && suppressNextAutoSelectEchoRef.current?.inputKey === selectorInputKey
            ) {
                suppressNextAutoSelectEchoRef.current = null;
                return;
            }

            const requestSeq = selectorRequestSeqRef.current + 1;
            selectorRequestSeqRef.current = requestSeq;
            const requestEntityId = currentEntity.entityId;
            const requestSelectedTripId = externalSelectedTripId?.toString() || '';
            const requestLiveStatusVersion = liveStatusVersionRef.current;
            const isCurrentSelectorRequest = () => (
                selectorRequestSeqRef.current === requestSeq
                && currentEntityIdRef.current === requestEntityId?.toString()
            );

            setIsLoadingList(true);
            try {
                const params = {
                    relevanceMode,
                    limit: relevanceMode === 'tracking' ? 20 : (relevanceMode === 'expanded' ? 100 : 50)
                };
                if (projection) params.projection = projection;
                if (includeSeries !== undefined) params.includeSeries = includeSeries;
                if (tripStatuses.length > 0) params.tripStatus = tripStatuses.join(',');
                if (externalSelectedTripId && relevanceMode !== 'history') {
                    params.selectedTripId = externalSelectedTripId;
                }
                if (relevanceMode === 'history') {
                    if (historyFilters.routeId) params.routeId = historyFilters.routeId;
                    if (historyFilters.search) params.search = historyFilters.search;
                    if (historyFilters.tripStatus) params.tripStatus = historyFilters.tripStatus;
                    if (historyFilters.startDate) params.startDate = historyFilters.startDate;
                    if (historyFilters.endDate) params.endDate = historyFilters.endDate;
                }

                const response = await axios.get(`/api/trips/${requestEntityId}/selector`, { params });

                if (!isCurrentSelectorRequest()) {
                    return;
                }

                const payload = response.data?.data;
                const fetchedTrips = Array.isArray(payload) ? payload : (payload?.trips || []);
                const reconciledTrips = fetchedTrips
                    .map((trip) => {
                        const liveStatus = latestLiveStatusByTripRef.current.get(trip.id?.toString());
                        if (!liveStatus || liveStatus.version <= requestLiveStatusVersion) {
                            return trip;
                        }
                        return {
                            ...trip,
                            status: liveStatus.status,
                            selected_archived_type:
                                liveStatus.archivedType || trip.selected_archived_type
                        };
                    })
                    .filter((trip) => selectionMode !== 'row' || isRowEligible(trip));

                // Request sequence protects only against newer HTTP requests.
                // Reconcile WebSocket statuses observed after this request
                // started so its older snapshot cannot restore an ineligible
                // assignment row or make it eligible for auto-selection.
                tripsRef.current = reconciledTrips;
                translatedTripsRef.current = reconciledTrips;
                setTrips(reconciledTrips);
                setTranslatedTrips(reconciledTrips);
                setSeries(Array.isArray(payload?.series) ? payload.series : []);

                const suppressAutoSelectEcho = (tripId) => {
                    suppressNextAutoSelectEchoRef.current = {
                        tripId: tripId?.toString() || '',
                        inputKey: buildSelectorInputKey({
                            entityId: requestEntityId,
                            relevanceMode,
                            historyFilters,
                            projection,
                            includeSeries,
                            tripStatuses,
                            refreshKey,
                            language: i18n.language,
                            multiLanguageEnabled: entitySettings?.multi_language_enabled,
                            defaultLanguage: entitySettings?.entity_default_language
                        })
                    };
                };

                const selectedRowId = externalSelectedTripId || selectedTripIdRef.current;
                if (selectionMode === 'row' && selectedRowId) {
                    await resolveTripSelection(selectedRowId, {
                        resetOnError: false,
                        source: 'list'
                    });
                }

                const shouldAutoSelect =
                    relevanceMode === 'tracking' &&
                    !externalSelectedTripId &&
                    !selectedTripIdRef.current &&
                    !autoSelectedRef.current &&
                    reconciledTrips.length > 0;

                if (shouldAutoSelect) {
                    autoSelectedRef.current = true;
                    suppressAutoSelectEcho(reconciledTrips[0].id);
                    resolveTripSelection(reconciledTrips[0].id, {
                        resetOnError: false,
                        source: 'auto',
                        selectedOptionSnapshot: { trip: reconciledTrips[0] }
                    });
                }

                translateTrips(reconciledTrips)
                    .then((translatedData) => {
                        if (isCurrentSelectorRequest()) {
                            const translatedById = new Map(
                                translatedData.map((trip) => [trip.id?.toString(), trip])
                            );
                            // Translation may resolve after a live status
                            // update removed or changed a row. Apply only the
                            // translated display field to the current rows so
                            // an older snapshot cannot restore terminal data.
                            setTranslatedTrips((currentTrips) => {
                                const reconciledTrips = currentTrips.map((trip) => {
                                    const translatedTrip = translatedById.get(trip.id?.toString());
                                    return translatedTrip?.route_name
                                        ? { ...trip, route_name: translatedTrip.route_name }
                                        : trip;
                                });
                                translatedTripsRef.current = reconciledTrips;
                                return reconciledTrips;
                            });
                        }
                    })
                    .catch((translationError) => {
                        console.error('[TripSelector] Error fetching translations:', translationError);
                    });
                onListRefresh?.({
                    success: true,
                    entityId: requestEntityId,
                    refreshKey,
                    selectedTripId: requestSelectedTripId,
                    trips: reconciledTrips
                });
            } catch (error) {
                if (!isCurrentSelectorRequest()) {
                    return;
                }
                console.error('Error fetching trips:', error);
                toast.error(t(
                    error?.code === 'ERR_NETWORK'
                        ? 'tripSelectorNetworkError'
                        : 'Failed to fetch my trips'
                ));
                tripsRef.current = [];
                translatedTripsRef.current = [];
                setTrips([]);
                setTranslatedTrips([]);
                setSeries([]);
                if (
                    selectionMode === 'row'
                    && (
                        selectedTripIdRef.current
                        || externalSelectedTripId
                    )
                ) {
                    if (externalSelectedTripId) {
                        suppressNextFailedSelectionClearRef.current = {
                            inputKey: selectorInputKey
                        };
                    }
                    selectedTripIdRef.current = '';
                    setSelectedTripId('');
                    onSelectTrip(null);
                    onSelectionInvalidated?.();
                }
                onListRefresh?.({
                    success: false,
                    entityId: requestEntityId,
                    refreshKey,
                    selectedTripId: requestSelectedTripId,
                    error
                });
            } finally {
                if (isCurrentSelectorRequest()) {
                    setIsLoadingList(false);
                }
            }
        };

        fetchAndTranslateTrips();
    }, [
        currentEntity?.entityId,
        entitySettings?.multi_language_enabled,
        entitySettings?.entity_default_language,
        externalSelectedTripId,
        includeSeries,
        resolveTripSelection,
        historyFilters,
        i18n.language,
        isHistoryMode,
        isTripPickerExpanded,
        isRowEligible,
        onListRefresh,
        onSelectTrip,
        onSelectionInvalidated,
        projection,
        refreshKey,
        selectionMode,
        tripStatuses,
        selectorPollVersion,
        t
    ]);

    useEffect(() => {
        // History queries are explicitly user-driven and are not affected by
        // live Delay ordering. Keep the bounded convergence timer/listener on
        // the tracking and expanded selector modes only.
        if (isHistoryMode) {
            return undefined;
        }

        const refreshVisibleSelector = () => {
            if (document.visibilityState === 'visible') {
                setSelectorPollVersion((version) => version + 1);
            }
        };
        const interval = window.setInterval(refreshVisibleSelector, 60000);
        window.addEventListener('focus', refreshVisibleSelector);
        document.addEventListener('visibilitychange', refreshVisibleSelector);
        const handleDelay = (payload) => {
            if (payload?.entityId?.toString() === currentEntity?.entityId?.toString()) {
                refreshVisibleSelector();
            }
        };
        WebSocketEvents.on('trip_delay_updated', handleDelay);
        return () => {
            window.clearInterval(interval);
            window.removeEventListener('focus', refreshVisibleSelector);
            document.removeEventListener('visibilitychange', refreshVisibleSelector);
            WebSocketEvents.removeListener('trip_delay_updated', handleDelay);
        };
    }, [currentEntity?.entityId, isHistoryMode]);

    useEffect(() => {
        const handleStatusUpdate = (data) => {
            if (!data.entityId || data.entityId.toString() !== currentEntity?.entityId?.toString()) {
                return;
            }

            const liveStatusVersion = liveStatusVersionRef.current + 1;
            liveStatusVersionRef.current = liveStatusVersion;
            latestLiveStatusByTripRef.current.set(data.tripId.toString(), {
                version: liveStatusVersion,
                status: data.status,
                archivedType: data.archived_type
            });

            const updateTrip = (trip) =>
                trip.id.toString() === data.tripId.toString()
                    ? {
                        ...trip,
                        status: data.status,
                        selected_archived_type: data.archived_type || trip.selected_archived_type
                    }
                    : trip;
            const existingRow = selectedTrip?.id?.toString() === data.tripId.toString()
                ? selectedTrip
                : tripsRef.current.find((trip) => trip.id?.toString() === data.tripId.toString());
            const updatedSelectedRow = existingRow ? updateTrip(existingRow) : null;

            if (selectionMode === 'row' && updatedSelectedRow && !isRowEligible(updatedSelectedRow)) {
                const keepOtherTrips = (trip) => trip.id?.toString() !== data.tripId.toString();
                setTrips(prevTrips => (prevTrips || []).filter(keepOtherTrips));
                setTranslatedTrips(prevTrips => (prevTrips || []).filter(keepOtherTrips));
                if (selectedTripIdRef.current?.toString() === data.tripId.toString()) {
                    selectedTripIdRef.current = '';
                    setSelectedTripId('');
                    onSelectTrip(null);
                    onSelectionInvalidated?.();
                }
                return;
            }

            setTrips(prevTrips => (prevTrips || []).map(updateTrip));
            setTranslatedTrips(prevTrips => (prevTrips || []).map(updateTrip));

            if (selectedTripIdRef.current?.toString() === data.tripId.toString()) {
                if (selectedTrip?.id?.toString() === data.tripId.toString()) {
                    onSelectTrip(updatedSelectedRow);
                } else {
                    resolveTripSelection(data.tripId, {
                        resetOnError: false,
                        source: 'websocket',
                        selectedOptionSnapshot: updatedSelectedRow
                            ? { trip: updatedSelectedRow }
                            : null
                    });
                }
            }
        };

        WebSocketEvents.on('status_update', handleStatusUpdate);
        return () => {
            WebSocketEvents.removeListener('status_update', handleStatusUpdate);
        };
    }, [
        currentEntity?.entityId,
        isRowEligible,
        onSelectTrip,
        onSelectionInvalidated,
        resolveTripSelection,
        selectedTrip,
        selectionMode
    ]);

    const colors = {
        scheduled: 'gray',
        dispatched: '#EAB308',
        in_progress: '#3B82F6',
        completed: '#22C55E',
        canceled: '#EF4444'
    };

    const getStatusColor = (status) => colors[status] || 'gray';

    const tripsToDisplay = useMemo(() => {
        const byId = new Map();
        if (!isHistoryMode && selectedTrip?.id && isRowEligible(selectedTrip)) {
            byId.set(Number(selectedTrip.id), selectedTrip);
        }
        (translatedTrips || trips || []).forEach((trip) => {
            if (isRowEligible(trip)) {
                byId.set(Number(trip.id), trip);
            }
        });
        return Array.from(byId.values());
    }, [isHistoryMode, isRowEligible, selectedTrip, translatedTrips, trips]);

    const getGroupLabel = (groupKey) => {
        const labels = {
            now: t('tripGroup.now'),
            today: t('tripGroup.today'),
            upcoming: t('tripGroup.upcoming'),
            earlier_today: t('tripGroup.earlierToday'),
            past: t('tripGroup.past')
        };
        return labels[groupKey] || labels.past;
    };

    const getTripGroup = (trip) => {
        if (trip.option_group && GROUP_ORDER.includes(trip.option_group)) return trip.option_group;
        if (trip.relevance_bucket && GROUP_ORDER.includes(trip.relevance_bucket)) return trip.relevance_bucket;
        if (ACTIVE_STATUSES.has(trip.status)) return 'now';
        if (trip.status === 'scheduled') return 'upcoming';
        return 'past';
    };

    const getTripDisplayDate = (trip) => (
        ['scheduled', 'dispatched'].includes(trip.status) && trip.effective_trip_date
            ? trip.effective_trip_date
            : trip.trip_date
    );
    const getTripDisplayTime = (trip) => (
        ['scheduled', 'dispatched'].includes(trip.status) && trip.effective_departure_time
            ? trip.effective_departure_time
            : trip.departure_time
    );

    const formatTripSearchLabel = (trip) => [
        trip.route_name,
        getTripDisplayDate(trip) ? formatDate(getTripDisplayDate(trip)) : '',
        getTripDisplayTime(trip) ? formatTimeString(getTripDisplayTime(trip)) : '',
        trip.status ? t(`tripStatus.${trip.status}`) : '',
        trip.driver_name,
        trip.vehicle_name
    ].filter(Boolean).join(' ');

    const buildTripOption = (trip) => ({
        value: trip.id,
        trip,
        group: getTripGroup(trip),
        searchLabel: formatTripSearchLabel(trip)
    });

    const flatOptions = tripsToDisplay.map(buildTripOption);

    const groupedOptions = GROUP_ORDER
        .map(groupKey => ({
            label: getGroupLabel(groupKey),
            options: flatOptions.filter(option => option.group === groupKey)
        }))
        .filter(group => group.options.length > 0);

    const selectedOption = flatOptions.find(
        option => option.value?.toString() === selectedTripId?.toString()
    ) || null;
    const hasPendingManualRequest = pendingDetailRequestRef.current?.source === 'manual';
    const pendingManualOption = hasPendingManualRequest
        ? pendingDetailRequestRef.current.selectedOptionSnapshot
        : null;
    const pendingManualTrip = pendingManualOption?.trip || null;
    const rawSelectedTripData = pendingManualTrip
        || (hasPendingManualRequest ? selectedOption?.trip || selectedTrip : selectedTrip || selectedOption?.trip)
        || (!isTripPickerExpanded && !isHistoryMode ? tripsToDisplay[0] : null);
    const translatedSelectedTripData = rawSelectedTripData
        ? (translatedTrips || []).find((trip) => (
            trip.id?.toString() === rawSelectedTripData.id?.toString()
            || (
                trip.route_id !== undefined
                && rawSelectedTripData.route_id !== undefined
                && trip.route_id?.toString() === rawSelectedTripData.route_id?.toString()
            )
        ))
        : null;
    const selectedTripData = rawSelectedTripData
        ? {
            ...rawSelectedTripData,
            option_group: rawSelectedTripData.option_group ?? pendingManualTrip?.option_group ?? selectedOption?.trip?.option_group,
            relevance_bucket: rawSelectedTripData.relevance_bucket ?? pendingManualTrip?.relevance_bucket ?? selectedOption?.trip?.relevance_bucket,
            route_name: translatedSelectedTripData?.route_name || rawSelectedTripData.route_name
        }
        : rawSelectedTripData;
    const upcomingTrips = tripsToDisplay
        .filter(trip =>
            ['today', 'upcoming'].includes(getTripGroup(trip))
            && trip.id?.toString() !== selectedTripData?.id?.toString()
        )
        .slice(0, 3);
    const isDarkMode = theme === 'dark';
    const currentMode = isHistoryMode ? 'history' : (isTripPickerExpanded ? 'expanded' : 'tracking');
    const selectedCardGroup = selectedTripData ? getTripGroup(selectedTripData) : null;
    const selectedCardLabel = (() => {
        if (selectedTripData?.status === 'completed') return t('recentTrip');
        if (ACTIVE_STATUSES.has(selectedTripData?.status)) return t('currentTrip');
        if (selectedTripData?.status === 'scheduled' && selectedCardGroup === 'earlier_today') return t('todaysTrip');
        return t('nextTrip');
    })();
    const showTrackingLoadingCard = currentMode === 'tracking' && isLoadingList && !selectedTripData;
    const hasMultipleRoutes = routeOptions.length > 1;
    const historyGridClassName = hasMultipleRoutes
        ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2'
        : 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2';

    const formatOptionLabel = ({ trip }) => (
        <div className="flex flex-wrap items-center gap-1 text-sm">
            <span style={{ color: '#3B82F6' }}>{trip.route_name}</span>
            <span style={{ color: '#6B7280' }}>|</span>
            <span style={{ color: '#22C55E' }}>{formatDate(getTripDisplayDate(trip))}</span>
            <span style={{ color: '#6B7280' }}>|</span>
            <span style={{ color: '#8B5CF6' }}>{formatTimeString(getTripDisplayTime(trip))}</span>
            <span style={{ color: '#6B7280' }}>|</span>
            <span style={{ color: getStatusColor(trip.status) }}>{t(`tripStatus.${trip.status}`)}</span>
        </div>
    );

    const filterOption = (option, inputValue) => {
        if (!inputValue) return true;
        return option.data.searchLabel.toLowerCase().includes(inputValue.toLowerCase());
    };

    const handleTripSelect = async (selectedOptionValue) => {
        if (!selectedOptionValue) {
            detailRequestSeqRef.current += 1;
            pendingDetailRequestRef.current = null;
            setSelectedTripId('');
            selectedTripIdRef.current = '';
            onSelectTrip(null);
            return;
        }
        await resolveTripSelection(selectedOptionValue.value, {
            source: 'manual',
            selectedOptionSnapshot: selectedOptionValue
        });
    };

    const updateHistoryFilter = (field, value) => {
        setHistoryFilters(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const openExpandedPicker = () => {
        setIsTripPickerExpanded(true);
        setIsHistoryMode(false);
    };

    const closeExpandedPicker = () => {
        setIsTripPickerExpanded(false);
        setIsHistoryMode(false);
    };

    const openHistoryMode = () => {
        if (!allowHistory) return;
        setIsTripPickerExpanded(true);
        setIsHistoryMode(true);
    };

    const customStyles = {
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected
                ? (isDarkMode ? getColor('surface') : '#F3F4F6')
                : state.isFocused
                    ? (isDarkMode ? '#4B5563' : '#F9FAFB')
                    : (isDarkMode ? getColor('background') : '#FFFFFF'),
            color: getColor('text'),
            cursor: 'pointer'
        }),
        control: (provided) => ({
            ...provided,
            backgroundColor: isDarkMode ? getColor('background') : '#FFFFFF',
            border: `1px solid ${isDarkMode ? getColor('border') : '#D1D5DB'}`,
            boxShadow: 'none',
            color: getColor('text'),
            '&:hover': {
                border: `1px solid ${isDarkMode ? '#6B7280' : '#9CA3AF'}`
            }
        }),
        menu: (provided) => ({
            ...provided,
            backgroundColor: isDarkMode ? getColor('background') : '#FFFFFF',
            border: `1px solid ${isDarkMode ? getColor('border') : '#D1D5DB'}`,
            boxShadow: isDarkMode
                ? '0 10px 15px -3px rgba(0, 0, 0, 0.3), 0 4px 6px -2px rgba(0, 0, 0, 0.2)'
                : '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
            top: '100%',
            marginTop: '8px'
        }),
        menuList: (provided) => ({
            ...provided,
            padding: '4px'
        }),
        singleValue: (provided) => ({
            ...provided,
            color: getColor('text')
        }),
        placeholder: (provided) => ({
            ...provided,
            color: isDarkMode ? getColor('textSecondary') : '#6B7280'
        })
    };

    return (
        <div className="space-y-3">
            {selectedTripData ? (
                <div className="border border-gray-200 dark:border-gray-700 rounded-md px-4 py-3 bg-white dark:bg-gray-900">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                            <div className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">
                                {selectedCardLabel}
                            </div>
                            <div className="font-semibold text-gray-900 dark:text-gray-100">
                                {selectedTripData.route_name}
                            </div>
                            <div className="text-sm text-gray-600 dark:text-gray-300">
                                {formatDate(getTripDisplayDate(selectedTripData))} | {formatTimeString(getTripDisplayTime(selectedTripData))}
                            </div>
                        </div>
                        <span
                            className="text-sm font-medium"
                            style={{ color: getStatusColor(selectedTripData.status) }}
                        >
                            {t(`tripStatus.${selectedTripData.status}`)}
                        </span>
                    </div>
                </div>
            ) : showTrackingLoadingCard ? (
                <div
                    data-testid="trip-selector-loading-card"
                    className="border border-gray-200 dark:border-gray-700 rounded-md px-4 py-3 bg-white dark:bg-gray-900 min-h-[76px]"
                >
                    <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                            <div className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">
                                {t('loadingTrips')}
                            </div>
                            <div className="font-semibold text-gray-900 dark:text-gray-100">
                                {t('loadingTrips')}
                            </div>
                            <div className="text-sm text-gray-600 dark:text-gray-300">
                                {t('selectATrip')}
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                !isLoadingList && (
                    <div className="border border-gray-200 dark:border-gray-700 rounded-md px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                        {t('noRelevantTrips')}
                    </div>
                )
            )}

            {isTripPickerExpanded && !isHistoryMode && upcomingTrips.length > 0 && (
                <div className="space-y-2">
                    <div className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">
                        {t('upcomingTrips')}
                    </div>
                    <div className="space-y-1">
                        {upcomingTrips.map((trip) => (
                            <button
                                key={trip.id}
                                type="button"
                                data-testid={`upcoming-trip-${trip.id}`}
                                className="w-full text-left px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md text-sm hover:bg-gray-50 dark:hover:bg-gray-800"
                                onClick={() => resolveTripSelection(trip.id, {
                                    source: 'manual',
                                    selectedOptionSnapshot: buildTripOption(trip)
                                })}
                            >
                                <span className="font-medium">{trip.route_name}</span>
                                <span className="text-gray-500 dark:text-gray-400">
                                    {' '}| {formatDate(getTripDisplayDate(trip))} | {formatTimeString(getTripDisplayTime(trip))}
                                </span>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {isTripPickerExpanded && !isHistoryMode && series.length > 0 && (
                <details className="border border-gray-200 dark:border-gray-700 rounded-md px-4 py-3">
                    <summary className="cursor-pointer text-sm font-semibold text-gray-800 dark:text-gray-100">
                        {t('recurringSeries')}
                    </summary>
                    <div className="mt-3 space-y-2">
                        {series.map((item) => (
                            <div key={item.series_id} className="text-sm text-gray-700 dark:text-gray-200">
                                <div className="font-medium">{item.route_name}</div>
                                <div className="text-gray-500 dark:text-gray-400">
                                    {(item.selected_days || []).join(', ') || t(`recurringType.${item.recurring_type}`)}
                                    {' '}| {formatTimeString(item.trip_time)}
                                </div>
                                {item.next_trip_id && (
                                    <div className="text-gray-500 dark:text-gray-400">
                                        {t('nextTrip')}: {formatDate(item.next_trip_date)} | {formatTimeString(item.next_trip_time)} | {item.upcoming_count} {t('upcomingTripsCount')}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </details>
            )}

            {isHistoryMode && (
                <div className={historyGridClassName}>
                    {hasMultipleRoutes && (
                        <select
                            className="px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                            aria-label={t('route')}
                            value={historyFilters.routeId}
                            onChange={(event) => updateHistoryFilter('routeId', event.target.value)}
                        >
                            <option value="">{t('allRoutes')}</option>
                            {routeOptions.map(route => (
                                <option key={route.id} value={route.id}>
                                    {formatHistoryRouteOptionLabel(route)}
                                </option>
                            ))}
                        </select>
                    )}
                    <input
                        type="date"
                        className="px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                        aria-label={t('startDate')}
                        value={historyFilters.startDate}
                        onChange={(event) => updateHistoryFilter('startDate', event.target.value)}
                    />
                    <input
                        type="date"
                        className="px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                        aria-label={t('endDate')}
                        value={historyFilters.endDate}
                        onChange={(event) => updateHistoryFilter('endDate', event.target.value)}
                    />
                    <select
                        className="px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                        aria-label={t('tripStatusFilter')}
                        value={historyFilters.tripStatus}
                        onChange={(event) => updateHistoryFilter('tripStatus', event.target.value)}
                    >
                        <option value="">{t('allStatuses')}</option>
                        {HISTORY_STATUSES.map(status => (
                            <option key={status} value={status}>
                                {t(`tripStatus.${status}`)}
                            </option>
                        ))}
                    </select>
                    <input
                        type="search"
                        className="px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                        placeholder={t('searchTrips')}
                        value={historyFilters.search}
                        onChange={(event) => updateHistoryFilter('search', event.target.value)}
                    />
                </div>
            )}

            <div className="flex flex-wrap justify-end gap-3">
                <button
                    type="button"
                    className="text-sm font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400"
                    onClick={isTripPickerExpanded ? closeExpandedPicker : openExpandedPicker}
                >
                    {isTripPickerExpanded ? t('lessTrips') : t('moreTrips')}
                </button>
                {allowHistory && (
                    <button
                        type="button"
                        className="text-sm font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400"
                        onClick={openHistoryMode}
                    >
                        {isHistoryMode ? t('findPastTrip') : t('tripHistory')}
                    </button>
                )}
            </div>

            {currentMode !== 'tracking' && (
                <Select
                    placeholder={isLoadingDetails || isLoadingList ? t('loadingTrips') : t('selectATrip')}
                    options={groupedOptions}
                    onChange={handleTripSelect}
                    styles={customStyles}
                    isClearable={false}
                    isDisabled={isLoadingDetails || isLoadingList}
                    value={pendingManualOption || selectedOption}
                    isLoading={isLoadingDetails || isLoadingList}
                    loadingMessage={() => <div>{t('loadingTrips')}</div>}
                    formatOptionLabel={formatOptionLabel}
                    filterOption={filterOption}
                />
            )}
        </div>
    );
};

export default React.memo(TripSelector);
